import type { YouTubeVideo } from '@domain/models/youtube'
import { YouTubeErrorConverter } from '@core/services/youtube/converters/youtube-error-converter'

const CHANNEL_ID = 'UCN2A0vGOY6j13Ed1rV64gPQ'
const RSS_URL = `https://www.youtube.com/feeds/videos.xml?channel_id=${CHANNEL_ID}`
const UPLOADS_PLAYLIST_ID = CHANNEL_ID.startsWith('UC')
  ? `UU${CHANNEL_ID.slice(2)}`
  : ''
const YOUTUBE_DATA_API_URL = 'https://www.googleapis.com/youtube/v3/playlistItems'
const YOUTUBE_VIDEOS_API_URL = 'https://www.googleapis.com/youtube/v3/videos'
const YOUTUBE_API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY?.trim()

// Public CORS proxies — try multiple in case one is down
const CORS_PROXIES = [
  (url: string) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
  (url: string) => `https://corsproxy.io/?${encodeURIComponent(url)}`,
]

const CACHE_KEY = 'yt_latest_videos_v2'
const DATA_API_BACKOFF_KEY = 'yt_data_api_backoff_until_v1'
const CACHE_TTL_MS = 60 * 60 * 1000 // 1 hour
const DATA_API_BACKOFF_TTL_MS = 60 * 60 * 1000 // 1 hour
const FETCH_TIMEOUT_MS = 10000
const SHORT_MAX_DURATION_SECONDS = 120

let dataApiBackoffUntil = 0

interface CachedYouTubeVideo {
  id: string
  title: string
  publishedAt: string
  durationSeconds?: number
  isShort?: boolean
}

interface CachedYouTubePayload {
  videos: CachedYouTubeVideo[]
  ts: number
  shortMaxDurationSeconds: number
}

interface YouTubeVideoMetadata {
  durationSeconds?: number
  isShort?: boolean
}

function chunkArray<T>(items: T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) {
    return [items]
  }

  const chunks: T[][] = []
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize))
  }
  return chunks
}

function shouldBackOffAfterStatus(status: number): boolean {
  return status === 403 || status === 429
}

function getStoredDataApiBackoffUntil(): number {
  try {
    const storedValue = sessionStorage.getItem(DATA_API_BACKOFF_KEY)
    const parsedValue = storedValue ? Number.parseInt(storedValue, 10) : 0
    return Number.isFinite(parsedValue) ? parsedValue : 0
  } catch {
    return 0
  }
}

function isDataApiBackedOff(): boolean {
  const now = Date.now()
  if (dataApiBackoffUntil > now) {
    return true
  }

  const storedBackoffUntil = getStoredDataApiBackoffUntil()
  if (storedBackoffUntil > now) {
    dataApiBackoffUntil = storedBackoffUntil
    return true
  }

  return false
}

function backOffDataApiRequests(): void {
  const backoffUntil = Date.now() + DATA_API_BACKOFF_TTL_MS
  dataApiBackoffUntil = backoffUntil
  try {
    sessionStorage.setItem(DATA_API_BACKOFF_KEY, String(backoffUntil))
  } catch {
    // sessionStorage may be unavailable.
  }
}

function backOffDataApiRequestsIfNeeded(response: Response): void {
  if (shouldBackOffAfterStatus(response.status)) {
    backOffDataApiRequests()
  }
}

function parseIso8601DurationToSeconds(duration: string): number | null {
  const normalized = duration.trim().toUpperCase()
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(normalized)
  if (!match) {
    return null
  }

  const hours = match[1] ? Number.parseInt(match[1], 10) : 0
  const minutes = match[2] ? Number.parseInt(match[2], 10) : 0
  const seconds = match[3] ? Number.parseInt(match[3], 10) : 0

  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    !Number.isFinite(seconds)
  ) {
    return null
  }

  return hours * 3600 + minutes * 60 + seconds
}

function toYouTubeVideoOrNull(input: unknown): YouTubeVideo | null {
  if (!input || typeof input !== 'object') {
    return null
  }

  const snippet = (input as Record<string, unknown>)['snippet']
  if (!snippet || typeof snippet !== 'object') {
    return null
  }

  const snippetRecord = snippet as Record<string, unknown>
  const resourceId = snippetRecord['resourceId']
  const resourceRecord =
    resourceId && typeof resourceId === 'object'
      ? (resourceId as Record<string, unknown>)
      : null
  const id =
    typeof resourceRecord?.['videoId'] === 'string'
      ? resourceRecord['videoId'].trim()
      : ''
  const title =
    typeof snippetRecord['title'] === 'string'
      ? snippetRecord['title'].trim()
      : ''
  const publishedRaw =
    typeof snippetRecord['publishedAt'] === 'string'
      ? snippetRecord['publishedAt']
      : ''
  const publishedAt = publishedRaw ? new Date(publishedRaw) : null

  if (!id || !title || !publishedAt || Number.isNaN(publishedAt.getTime())) {
    return null
  }

  return { id, title, publishedAt }
}

export class YouTubeService {
  private static instance: YouTubeService

  private constructor() {}

  public static getInstance(): YouTubeService {
    if (!YouTubeService.instance) {
      YouTubeService.instance = new YouTubeService()
    }
    return YouTubeService.instance
  }

  private getCache(): YouTubeVideo[] | null {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY)
      if (!raw) return null
      const payload = JSON.parse(raw) as Partial<CachedYouTubePayload>
      const videos = Array.isArray(payload.videos) ? payload.videos : null
      const ts = typeof payload.ts === 'number' ? payload.ts : null
      const cachedShortMaxDurationSeconds =
        typeof payload.shortMaxDurationSeconds === 'number'
          ? payload.shortMaxDurationSeconds
          : null

      if (!videos || ts === null) return null
      if (Date.now() - ts > CACHE_TTL_MS) return null
      if (cachedShortMaxDurationSeconds !== SHORT_MAX_DURATION_SECONDS) {
        return null
      }

      const normalizedVideos = videos
        .map((video): YouTubeVideo | null => {
          const publishedAt = new Date(video.publishedAt)
          if (!video.id || !video.title || Number.isNaN(publishedAt.getTime())) {
            return null
          }

          const durationSeconds =
            typeof video.durationSeconds === 'number' &&
            Number.isFinite(video.durationSeconds) &&
            video.durationSeconds >= 0
              ? Math.floor(video.durationSeconds)
              : undefined
          const isShort =
            typeof durationSeconds === 'number'
              ? durationSeconds <= SHORT_MAX_DURATION_SECONDS
              : typeof video.isShort === 'boolean'
                ? video.isShort
                : undefined

          return {
            id: video.id,
            title: video.title,
            publishedAt,
            ...(typeof durationSeconds === 'number' ? { durationSeconds } : {}),
            ...(typeof isShort === 'boolean' ? { isShort } : {}),
          } satisfies YouTubeVideo
        })
        .filter((video): video is YouTubeVideo => video !== null)

      return normalizedVideos
    } catch {
      return null
    }
  }

  private setCache(videos: YouTubeVideo[]) {
    try {
      const serializedVideos: CachedYouTubeVideo[] = videos.map((video) => ({
        id: video.id,
        title: video.title,
        publishedAt: video.publishedAt.toISOString(),
        durationSeconds: video.durationSeconds,
        isShort: video.isShort,
      }))
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          videos: serializedVideos,
          ts: Date.now(),
          shortMaxDurationSeconds: SHORT_MAX_DURATION_SECONDS,
        } satisfies CachedYouTubePayload),
      )
    } catch {
      // sessionStorage may be unavailable.
    }
  }

  private getEntryTextByLocalName(entry: Element, localName: string): string | null {
    const normalizedName = localName.toLowerCase()
    for (const child of Array.from(entry.children)) {
      if (child.localName.toLowerCase() !== normalizedName) {
        continue
      }

      const value = child.textContent?.trim()
      if (value) {
        return value
      }
    }

    return null
  }

  private parseVideosFromXml(xml: string): YouTubeVideo[] {
    const parser = new DOMParser()
    const doc = parser.parseFromString(xml, 'application/xml')

    const entries = doc.querySelectorAll('entry')
    const videos: YouTubeVideo[] = []

    entries.forEach((entry) => {
      const videoId = this.getEntryTextByLocalName(entry, 'videoId')
      const title = this.getEntryTextByLocalName(entry, 'title')
      const publishedAtRaw = this.getEntryTextByLocalName(entry, 'published')
      const publishedAt = publishedAtRaw ? new Date(publishedAtRaw) : null

      if (
        videoId &&
        title &&
        publishedAt &&
        !Number.isNaN(publishedAt.getTime())
      ) {
        videos.push({
          id: videoId,
          title,
          publishedAt,
        })
      }
    })

    return videos
  }

  private async fetchXmlThroughProxy(proxyUrl: string): Promise<string | null> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
      const response = await fetch(proxyUrl, { signal: controller.signal })
      if (!response.ok) {
        return null
      }
      return await response.text()
    } catch {
      return null
    } finally {
      clearTimeout(timeoutId)
    }
  }

  private async fetchVideosViaYouTubeDataApi(count: number): Promise<YouTubeVideo[]> {
    if (!YOUTUBE_API_KEY || !UPLOADS_PLAYLIST_ID || count <= 0 || isDataApiBackedOff()) {
      return []
    }

    const videos: YouTubeVideo[] = []
    let nextPageToken: string | null = null
    const maxPages = Math.max(1, Math.ceil(count / 50) + 1)

    for (let page = 0; page < maxPages && videos.length < count; page += 1) {
      const params = new URLSearchParams({
        part: 'snippet',
        playlistId: UPLOADS_PLAYLIST_ID,
        maxResults: '50',
        key: YOUTUBE_API_KEY,
      })

      if (nextPageToken) {
        params.set('pageToken', nextPageToken)
      }

      const response = await fetch(`${YOUTUBE_DATA_API_URL}?${params.toString()}`)
      if (!response.ok) {
        backOffDataApiRequestsIfNeeded(response)
        return []
      }

      const payload = (await response.json()) as Record<string, unknown>
      const items = Array.isArray(payload['items']) ? payload['items'] : []
      for (const item of items) {
        const parsed = toYouTubeVideoOrNull(item)
        if (parsed) {
          videos.push(parsed)
        }
      }

      nextPageToken =
        typeof payload['nextPageToken'] === 'string'
          ? payload['nextPageToken']
          : null
      if (!nextPageToken) {
        break
      }
    }

    return this.enrichVideosWithMetadata(videos)
  }

  private async fetchVideoMetadataViaYouTubeDataApi(
    videoIds: string[],
  ): Promise<Record<string, YouTubeVideoMetadata>> {
    if (!YOUTUBE_API_KEY || videoIds.length === 0 || isDataApiBackedOff()) {
      return {}
    }

    const metadataByVideoId: Record<string, YouTubeVideoMetadata> = {}
    const chunks = chunkArray(videoIds, 50)

    for (const idsChunk of chunks) {
      const params = new URLSearchParams({
        part: 'contentDetails,status',
        id: idsChunk.join(','),
        key: YOUTUBE_API_KEY,
        maxResults: '50',
      })

      const response = await fetch(`${YOUTUBE_VIDEOS_API_URL}?${params.toString()}`)
      if (!response.ok) {
        backOffDataApiRequestsIfNeeded(response)
        if (shouldBackOffAfterStatus(response.status)) {
          break
        }
        continue
      }

      const payload = (await response.json()) as Record<string, unknown>
      const items = Array.isArray(payload['items']) ? payload['items'] : []

      for (const item of items) {
        if (!item || typeof item !== 'object') {
          continue
        }

        const itemRecord = item as Record<string, unknown>
        const videoId =
          typeof itemRecord['id'] === 'string' ? itemRecord['id'].trim() : ''
        if (!videoId) {
          continue
        }

        const contentDetails =
          itemRecord['contentDetails'] &&
          typeof itemRecord['contentDetails'] === 'object'
            ? (itemRecord['contentDetails'] as Record<string, unknown>)
            : null

        const durationRaw =
          contentDetails && typeof contentDetails['duration'] === 'string'
            ? contentDetails['duration']
            : ''
        const parsedDurationSeconds = durationRaw
          ? parseIso8601DurationToSeconds(durationRaw)
          : null
        const durationSeconds =
          typeof parsedDurationSeconds === 'number' ? parsedDurationSeconds : undefined

        metadataByVideoId[videoId] = {
          durationSeconds,
          isShort:
            typeof durationSeconds === 'number'
              ? durationSeconds <= SHORT_MAX_DURATION_SECONDS
              : undefined,
        }
      }
    }

    return metadataByVideoId
  }

  private async enrichVideosWithMetadata(
    videos: YouTubeVideo[],
  ): Promise<YouTubeVideo[]> {
    const uniqueVideoIds = Array.from(new Set(videos.map((video) => video.id).filter(Boolean)))
    if (uniqueVideoIds.length === 0) {
      return videos
    }

    const metadataByVideoId = await this.fetchVideoMetadataViaYouTubeDataApi(uniqueVideoIds)
    if (Object.keys(metadataByVideoId).length === 0) {
      return videos
    }

    return videos.map((video) => {
      const metadata = metadataByVideoId[video.id]
      if (!metadata) {
        return video
      }

      return {
        ...video,
        durationSeconds: metadata.durationSeconds,
        isShort: metadata.isShort,
      }
    })
  }

  private async fetchVideoPublishDatesViaYouTubeDataApi(
    videoIds: string[],
  ): Promise<Record<string, Date>> {
    if (!YOUTUBE_API_KEY || videoIds.length === 0 || isDataApiBackedOff()) {
      return {}
    }

    const publishDateByVideoId: Record<string, Date> = {}
    const chunks = chunkArray(videoIds, 50)

    for (const idsChunk of chunks) {
      const params = new URLSearchParams({
        part: 'snippet',
        id: idsChunk.join(','),
        key: YOUTUBE_API_KEY,
        maxResults: '50',
      })

      const response = await fetch(`${YOUTUBE_VIDEOS_API_URL}?${params.toString()}`)
      if (!response.ok) {
        backOffDataApiRequestsIfNeeded(response)
        if (shouldBackOffAfterStatus(response.status)) {
          break
        }
        continue
      }

      const payload = (await response.json()) as Record<string, unknown>
      const items = Array.isArray(payload['items']) ? payload['items'] : []
      for (const item of items) {
        if (!item || typeof item !== 'object') {
          continue
        }

        const itemRecord = item as Record<string, unknown>
        const videoId =
          typeof itemRecord['id'] === 'string' ? itemRecord['id'].trim() : ''
        if (!videoId) {
          continue
        }

        const snippet =
          itemRecord['snippet'] && typeof itemRecord['snippet'] === 'object'
            ? (itemRecord['snippet'] as Record<string, unknown>)
            : null

        const publishedAtRaw =
          snippet && typeof snippet['publishedAt'] === 'string'
            ? snippet['publishedAt']
            : ''
        const publishedAt = publishedAtRaw ? new Date(publishedAtRaw) : null
        if (!publishedAt || Number.isNaN(publishedAt.getTime())) {
          continue
        }

        publishDateByVideoId[videoId] = publishedAt
      }
    }

    return publishDateByVideoId
  }

  public async fetchLatestYouTubeVideos(count = 4): Promise<YouTubeVideo[]> {
    try {
      // Check cache first.
      const cached = this.getCache()
      if (cached && cached.length >= count) {
        return cached.slice(0, count)
      }

      // Prefer official API when key is configured; supports true pagination.
      const apiVideos = await this.fetchVideosViaYouTubeDataApi(count)
      if (apiVideos.length > 0) {
        this.setCache(apiVideos)
        return apiVideos.slice(0, count)
      }

      // Try each CORS proxy until one works.
      for (const proxyFn of CORS_PROXIES) {
        const xml = await this.fetchXmlThroughProxy(proxyFn(RSS_URL))
        if (!xml) {
          continue
        }

        const videos = await this.enrichVideosWithMetadata(this.parseVideosFromXml(xml))

        if (videos.length > 0) {
          this.setCache(videos)
          return videos.slice(0, count)
        }
      }

      // All proxies failed; caller may use feature-level fallback.
      return []
    } catch (error) {
      throw YouTubeErrorConverter.domainModel(error)
    }
  }

  public async fetchYouTubeVideoPublishDates(
    videoIds: string[],
  ): Promise<Record<string, Date>> {
    try {
      const normalizedVideoIds = Array.from(
        new Set(videoIds.map((item) => item.trim()).filter(Boolean)),
      )
      if (normalizedVideoIds.length === 0) {
        return {}
      }

      return await this.fetchVideoPublishDatesViaYouTubeDataApi(normalizedVideoIds)
    } catch {
      // Fallback to symptom created_time happens at feature layer.
      return {}
    }
  }
}

const youTubeService = YouTubeService.getInstance()

export async function fetchLatestYouTubeVideos(
  count = 4,
): Promise<YouTubeVideo[]> {
  return youTubeService.fetchLatestYouTubeVideos(count)
}

export async function fetchYouTubeVideoPublishDates(
  videoIds: string[],
): Promise<Record<string, Date>> {
  return youTubeService.fetchYouTubeVideoPublishDates(videoIds)
}
